import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAccepted } from '../lib/cookieConsent';

/**
 * Meta (Facebook) Pixel tracking for U&Fashion.
 *
 * The pixel is GDPR-gated: it only fires after the visitor accepts the
 * cookie banner. On accept we dynamically load fbevents.js and fire the
 * initial PageView (see /lib/cookieConsent.js). This hook then fires
 * an additional PageView on every SPA route change.
 *
 * Helper `trackAffiliateClick(brand)` can be imported from any
 * component to fire a `ViewContent` event when a visitor clicks an
 * affiliate CTA. That is the primary conversion signal we want Meta
 * to optimise the ad audience against.
 */
export default function useMetaPixel() {
  const location = useLocation();

  useEffect(() => {
    if (!hasAccepted()) return;
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search]);
}

/**
 * Fire a `ViewContent` conversion event whenever a visitor clicks
 * through to a partner store. Safe to call — it's a no-op if the
 * visitor has not accepted the cookie banner.
 */
export const trackAffiliateClick = (brand, categoryName) => {
  if (!hasAccepted()) return;
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq('track', 'ViewContent', {
      content_name: brand || 'affiliate-link',
      content_category: categoryName || 'fashion',
      content_type: 'product',
    });
  } catch (e) {
    // never let analytics break navigation
  }
};

/**
 * Fire a `Lead` event when a visitor completes the newsletter form.
 */
export const trackNewsletterLead = () => {
  if (!hasAccepted()) return;
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq('track', 'Lead', { content_name: 'newsletter-signup' });
  } catch (e) {
    // ignore
  }
};
